import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PrestigeConfiguration } from '@core';
import { repositoryHost } from '@repositories';
import { serviceHost } from '@services';
import { prestigeConfigurationQueryKey } from './query-keys';

type UsePrestigeConfigurationOptions = {
  enabled?: boolean;
};

export const usePrestigeConfiguration = (
  configId: string | undefined,
  options: UsePrestigeConfigurationOptions = {},
) => {
  const enabled = Boolean(options.enabled ?? true) && Boolean(configId);
  const queryClient = useQueryClient();

  const repository = useMemo(
    () =>
      repositoryHost.getPrestigeConfigurationRepository(
        serviceHost.getDatabaseService(),
      ),
    [],
  );

  const queryResult = useQuery<PrestigeConfiguration | null>({
    queryKey: configId ? prestigeConfigurationQueryKey(configId) : ['prestigeConfiguration'],
    enabled,
    queryFn: async () => {
      if (!configId) {
        return null;
      }

      return repository.get({ id: configId });
    },
  });

  useEffect(() => {
    if (!enabled || !configId) {
      return;
    }

    return repository.subscribe({
      id: configId,
      callback: (entry: PrestigeConfiguration | null) => {
        queryClient.setQueryData(prestigeConfigurationQueryKey(configId), entry);
      },
    });
  }, [configId, enabled, queryClient, repository]);

  return queryResult;
};
